import * as Sentry from "@sentry/react";
import { useAuthStore } from "@/store/auth.store";

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn || !import.meta.env.PROD) return;

  Sentry.init({
    dsn,
    sendDefaultPii: true,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
      // `main.tsx` pose son propre listener `unhandledrejection`, qui décrit le rejet (type
      // d'événement, service, état de connexion) avant de l'envoyer à Sentry — sans ça, un rejet
      // dont la raison est un `Event` DOM brut (ex. WebSocket fermé côté LiveKit) remonte comme
      // « Event `Event` (type=error) captured as promise rejection », sans aucune information
      // exploitable. Sans cette désactivation, l'intégration par défaut de Sentry pose SON PROPRE
      // listener sur le même événement et capture indépendamment la version brute : chaque rejet
      // produisait donc deux issues pour un seul incident, l'une utile et l'autre du bruit
      // (SUCCESSCHOOL-FRONTEND-V2-A). `onerror` reste actif, seul son filet redondant est coupé.
      Sentry.globalHandlersIntegration({ onunhandledrejection: false }),
    ],
    tracesSampleRate: 0.1,
    tracePropagationTargets: [/^\//, /^https?:\/\/.*succeschool/],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Bruit des navigateurs in-app (Instagram, Facebook, TikTok…) : ils injectent leurs propres
    // scripts de télémétrie (`navigation_performance_logger`, tantôt servi via `iabjs://`, tantôt
    // inliné dans le document) qui dialoguent avec la WebView native. Quand la WebView est détruite
    // pendant qu'un de ces scripts tourne encore, le pont lève selon la plateforme :
    //   - Android : « Java object is gone » / « Error invoking postMessage »
    //   - iOS     : « undefined is not an object (evaluating 'window.webkit.messageHandlers') »
    // Ça ne vient pas de notre code, l'utilisateur ne voit rien, rien à corriger côté SPA
    // (SUCCESCHOOL-FRONTEND-V2-…).
    ignoreErrors: [
      "Java object is gone",
      "Error invoking postMessage",
      "window.webkit.messageHandlers",
    ],
    beforeSend(event) {
      const frames = event.exception?.values?.flatMap((v) => v.stacktrace?.frames ?? []) ?? [];
      const fromInAppBrowserBridge = frames.some(
        (f) =>
          /iabjs:\/\/|navigation_performance_logger/.test(f.filename ?? "") ||
          /^(sendDataToNative|sendPageHideMessage|sendJsBlockingTimeMessage)$/.test(
            f.function ?? "",
          ),
      );
      return fromInAppBrowserBridge ? null : event;
    },
  });

  const applyUser = (user: ReturnType<typeof useAuthStore.getState>["user"]) => {
    if (user) {
      Sentry.setUser({ id: String(user.id), email: user.email ?? undefined });
    } else {
      Sentry.setUser(null);
    }
  };

  applyUser(useAuthStore.getState().user);
  useAuthStore.subscribe((state) => applyUser(state.user));
}
